import { useEffect, useState } from "react";
import { AlertCircle, Clock, Loader, PauseCircle, Square } from "lucide-react";
import type { AgentRunRecord } from "@racler/protocol";

function formatElapsed(startedAt: string): string {
  const diffSec = Math.max(0, Math.floor((Date.now() - new Date(startedAt).getTime()) / 1000));
  const mins = Math.floor(diffSec / 60);
  const secs = diffSec % 60;
  if (mins === 0) return `${secs}s`;
  return `${mins}m ${secs < 10 ? "0" : ""}${secs}s`;
}

const STAGE_LABEL: Record<string, string> = {
  started: "Starting…",
  thinking: "Thinking…",
  streaming: "Generating answer…",
  listFiles: "Listing files…",
  searchFiles: "Searching files…",
  readFile: "Reading file…",
  runCommand: "Executing command…",
  proposeCreateFile: "Proposing file creation…",
  proposeModifyFile: "Proposing file change…",
  proposeDeleteFile: "Proposing file deletion…",
  proposeRenameFile: "Proposing file rename…",
  awaiting_approval: "Awaiting approval",
  blocked_by_policy: "Blocked by policy",
  completed: "Completed",
  cancelled: "Stopped",
  interrupted: "Interrupted",
  failed: "Failed",
};

export function RunStatusBar({
  activeRun,
  running,
  onStop,
  onRetry,
}: {
  activeRun?: AgentRunRecord | null;
  running: boolean;
  onStop: () => void;
  onRetry: () => void;
}) {
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    if (!running || !activeRun?.startedAt) return;
    const interval = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(interval);
  }, [running, activeRun?.startedAt]);

  void now;

  if (!activeRun) return null;

  const isRunning = running || activeRun.status === "running";
  const isAwaiting = activeRun.status === "awaiting_approval";
  const isBlocked = activeRun.stage === "blocked_by_policy";
  const isInterrupted = activeRun.status === "interrupted";
  const isFailed = activeRun.status === "failed";
  const isCancelled = activeRun.status === "cancelled";

  // If completed or cancelled and no longer running, let the chat message speak for itself
  if (!isRunning && !isAwaiting && !isBlocked && !isInterrupted && !isFailed) {
    return null;
  }

  const stageLabel = STAGE_LABEL[activeRun.stage] ?? activeRun.stage;
  const elapsed = activeRun.startedAt ? formatElapsed(activeRun.startedAt) : "";

  return (
    <div
      className={`run-status-bar run-status-${activeRun.status}`}
      data-testid="run-status-bar"
      data-status={activeRun.status}
    >
      <div className="run-status-info">
        {isRunning && <Loader size={13} className="spin" aria-hidden />}
        {isAwaiting && <PauseCircle size={13} aria-hidden />}
        {(isInterrupted || isFailed || isBlocked) && <AlertCircle size={13} aria-hidden />}
        <span className="run-status-badge" data-testid="run-status-badge">
          {isRunning
            ? "Running"
            : isAwaiting
              ? "Awaiting approval"
              : isBlocked
                ? "Blocked by policy"
                : isInterrupted
                  ? "Interrupted"
                  : isFailed
                    ? "Failed"
                    : isCancelled
                      ? "Stopped"
                      : "Completed"}
        </span>
        {isRunning && <span className="run-stage-text" data-testid="run-stage-text">{stageLabel}</span>}
        {isRunning && elapsed && (
          <span className="run-elapsed" data-testid="run-elapsed" title="Elapsed time">
            <Clock size={11} aria-hidden style={{ verticalAlign: "middle", marginRight: 2 }} />
            {elapsed}
          </span>
        )}
        {isInterrupted && (
          <span className="muted small">
            {activeRun.error ?? "The response was interrupted."}
          </span>
        )}
      </div>
      <div className="run-status-actions">
        {isRunning && (
          <button
            type="button"
            className="run-stop-btn"
            onClick={onStop}
            title="Stop run"
            data-testid="run-stop-btn"
          >
            <Square size={11} fill="currentColor" aria-hidden />
            <span>Stop</span>
          </button>
        )}
        {(isInterrupted || isFailed) && (
          <button
            type="button"
            className="run-retry-btn"
            onClick={onRetry}
            data-testid="run-retry-btn"
          >
            Retry
          </button>
        )}
      </div>
    </div>
  );
}
